import { lazy, Suspense, type ComponentType } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { ProtectedRoute } from "@/features/auth/components/ProtectedRoute";
import { RoleBasedRoute } from "@/features/auth/components/RoleBasedRoute";
import { Layout } from "@/components/Layout";
import { LazyErrorBoundary } from "@/components/ui/LazyErrorBoundary";

// --- Helper: wraps a lazy component in Suspense + ErrorBoundary ---
function lazyPage(
  factory: () => Promise<Record<string, ComponentType>>,
  namedExport?: string
) {
  const LazyComponent = lazy(() =>
    factory().then((module) => {
      if (namedExport && namedExport in module) {
        return { default: module[namedExport] };
      }
      return module as { default: ComponentType };
    })
  );

  return function LazyPage() {
    return (
      <LazyErrorBoundary>
        <Suspense fallback={<div className="min-h-[200px]" />}>
          <LazyComponent />
        </Suspense>
      </LazyErrorBoundary>
    );
  };
}

// =============================================================================
// Lazy page imports - grouped by feature/role for optimal chunk splitting
// =============================================================================

// --- Auth pages (public, small - loaded on demand) ---
const LoginPage = lazyPage(
  () => import("@/features/auth/pages/LoginPage"),
  "LoginPage"
);
const ForgotPasswordPage = lazyPage(
  () => import("@/features/auth/pages/ForgotPasswordPage"),
  "ForgotPasswordPage"
);
const ResetPasswordPage = lazyPage(
  () => import("@/features/auth/pages/ResetPasswordPage"),
  "ResetPasswordPage"
);
const AcceptInvitationPage = lazyPage(
  () => import("@/features/auth/pages/AcceptInvitationPage"),
  "AcceptInvitationPage"
);
const UnauthorizedPage = lazyPage(
  () => import("@/features/auth/pages/UnauthorizedPage"),
  "UnauthorizedPage"
);

// --- Dashboard ---
const DashboardPage = lazyPage(
  () => import("@/features/dashboard/pages/DashboardPage"),
  "DashboardPage"
);

// --- Schedule pages (heavy: calendar, drag&drop, optimization) ---
const ScheduleListPage = lazyPage(
  () => import("@/features/schedule/pages/ScheduleListPage"),
  "ScheduleListPage"
);
const ScheduleDetailPage = lazyPage(
  () => import("@/features/schedule/pages/ScheduleDetailPage"),
  "ScheduleDetailPage"
);
const FinalizationPage = lazyPage(
  () => import("@/features/schedule/pages/FinalizationPage"),
  "FinalizationPage"
);
const ScheduleCalendarPage = lazyPage(
  () => import("@/features/schedule/components/calendar/ScheduleCalendarPage"),
  "ScheduleCalendarPage"
);
const SchedulePreviewPage = lazyPage(
  () => import("@/features/schedule/components/calendar/SchedulePreviewPage"),
  "SchedulePreviewPage"
);

// --- Dictionary Data ---
const DictionaryDataPage = lazyPage(
  () => import("@/features/dictionaryData/pages/DictionaryDataPage"),
  "DictionaryDataPage"
);

// --- Profile ---
const ProfilePage = lazyPage(
  () => import("@/features/profile/pages/ProfilePage"),
  "ProfilePage"
);

// --- Parent Approval ---
const ParentApprovalsPage = lazyPage(
  () => import("@/features/parentApproval/pages/ParentApprovalsPage"),
  "ParentApprovalsPage"
);
const ParentApprovalDetailsPage = lazyPage(
  () => import("@/features/parentApproval/pages/ParentApprovalDetailsPage"),
  "ParentApprovalDetailsPage"
);
const PublicApprovalPage = lazyPage(
  () => import("@/features/parentApproval/pages/PublicApprovalPage"),
  "PublicApprovalPage"
);

// --- Demo ---
const DemoHelpPage = lazyPage(
  () => import("@/features/demo/pages/DemoHelpPage"),
  "DemoHelpPage"
);

// --- Help ---
const HelpPage = lazyPage(
  () => import("@/features/help/pages/HelpPage"),
  "HelpPage"
);
const EmployeeHelpPage = lazyPage(
  () => import("@/features/help/pages/EmployeeHelpPage"),
  "EmployeeHelpPage"
);

// --- Timesheet ---
const TimesheetListPage = lazyPage(
  () => import("@/features/timesheet/pages/TimesheetListPage"),
  "TimesheetListPage"
);

// --- Reports (heavy: recharts) ---
const ReportsPage = lazyPage(
  () => import("@/features/reports/pages/ReportsPage"),
  "ReportsPage"
);
const EmployeeReportPage = lazyPage(
  () => import("@/features/reports/pages/EmployeeReportPage"),
  "EmployeeReportPage"
);

// --- Employees ---
const EmployeeListPage = lazyPage(
  () => import("@/features/employees/pages/EmployeeListPage"),
  "EmployeeListPage"
);

// --- Substitutions ---
const SubstitutionsListPage = lazyPage(
  () => import("@/features/substitutions/pages/SubstitutionsListPage"),
  "SubstitutionsListPage"
);
const SubstitutionDetailsPage = lazyPage(
  () => import("@/features/substitutions/pages/SubstitutionDetailsPage"),
  "SubstitutionDetailsPage"
);
const DepartureDetailsPage = lazyPage(
  () => import("@/features/substitutions/pages/DepartureDetailsPage"),
  "DepartureDetailsPage"
);

// --- Chat (admin) ---
const AdminChatPage = lazyPage(
  () => import("@/features/chat/pages/AdminChatPage"),
  "AdminChatPage"
);
const AdminChatGroupPage = lazyPage(
  () => import("@/features/chat/pages/AdminChatGroupPage"),
  "AdminChatGroupPage"
);

// --- Employee Portal ---
const EmployeeLayout = lazyPage(
  () => import("@/features/employee/layouts/EmployeeLayout"),
  "EmployeeLayout"
);
const EmployeeDashboardPage = lazyPage(
  () => import("@/features/employee/pages/EmployeeDashboardPage"),
  "EmployeeDashboardPage"
);
const EmployeeTimesheetPage = lazyPage(
  () => import("@/features/employee/pages/EmployeeTimesheetPage"),
  "EmployeeTimesheetPage"
);
const EmployeeSchedulePage = lazyPage(
  () => import("@/features/employee/pages/EmployeeSchedulePage"),
  "EmployeeSchedulePage"
);
const EmployeeChatPage = lazyPage(
  () => import("@/features/chat/pages/EmployeeChatPage"),
  "EmployeeChatPage"
);
const EmployeeChatGroupPage = lazyPage(
  () => import("@/features/chat/pages/EmployeeChatGroupPage"),
  "EmployeeChatGroupPage"
);

// --- Parent Chat ---
const ParentAuthPage = lazyPage(
  () => import("@/features/parentChat/pages/ParentAuthPage"),
  "ParentAuthPage"
);
const ParentLayout = lazyPage(
  () => import("@/features/parentChat/components/ParentLayout"),
  "ParentLayout"
);
const ParentGroupListPage = lazyPage(
  () => import("@/features/parentChat/pages/ParentGroupListPage"),
  "ParentGroupListPage"
);
const ParentChatPage = lazyPage(
  () => import("@/features/parentChat/pages/ParentChatPage"),
  "ParentChatPage"
);

// --- Super Admin Portal ---
const SuperAdminLayout = lazyPage(
  () => import("@/features/superAdmin/layouts/SuperAdminLayout"),
  "SuperAdminLayout"
);
const SuperAdminDashboardPage = lazyPage(
  () => import("@/features/superAdmin/pages/SuperAdminDashboardPage"),
  "SuperAdminDashboardPage"
);
const TenantListPage = lazyPage(
  () => import("@/features/superAdmin/pages/TenantListPage"),
  "TenantListPage"
);
const TenantDetailPage = lazyPage(
  () => import("@/features/superAdmin/pages/TenantDetailPage"),
  "TenantDetailPage"
);
const HivePlanListPage = lazyPage(
  () => import("@/features/superAdmin/pages/HivePlanListPage"),
  "HivePlanListPage"
);
const PurchaseOrderListPage = lazyPage(
  () => import("@/features/superAdmin/pages/PurchaseOrderListPage"),
  "PurchaseOrderListPage"
);
const PurchaseOrderDetailPage = lazyPage(
  () => import("@/features/superAdmin/pages/PurchaseOrderDetailPage"),
  "PurchaseOrderDetailPage"
);
const StatsPage = lazyPage(
  () => import("@/features/superAdmin/pages/StatsPage"),
  "StatsPage"
);
const HealthCheckPage = lazyPage(
  () => import("@/features/superAdmin/pages/HealthCheckPage"),
  "HealthCheckPage"
);
const HolidayListPage = lazyPage(
  () => import("@/features/superAdmin/pages/HolidayListPage"),
  "HolidayListPage"
);
const InvoiceListPage = lazyPage(
  () => import("@/features/superAdmin/pages/InvoiceListPage"),
  "InvoiceListPage"
);
const InvoiceDetailPage = lazyPage(
  () => import("@/features/superAdmin/pages/InvoiceDetailPage"),
  "InvoiceDetailPage"
);
const SubscriptionListPage = lazyPage(
  () => import("@/features/superAdmin/pages/SubscriptionListPage"),
  "SubscriptionListPage"
);
const SystemTasksPage = lazyPage(
  () => import("@/features/superAdmin/pages/SystemTasksPage"),
  "SystemTasksPage"
);

// --- Purchase ---
const PurchaseOrderPage = lazyPage(
  () => import("@/features/purchase/pages/PurchaseOrderPage"),
  "PurchaseOrderPage"
);
const PaymentSuccessPage = lazyPage(
  () => import("@/features/purchase/pages/PaymentSuccessPage"),
  "PaymentSuccessPage"
);
const PaymentErrorPage = lazyPage(
  () => import("@/features/purchase/pages/PaymentErrorPage"),
  "PaymentErrorPage"
);
const RenewalPage = lazyPage(
  () => import("@/features/purchase/pages/RenewalPage"),
  "RenewalPage"
);

// =============================================================================
// Router definition
// =============================================================================

export const router = createBrowserRouter([
  // Public routes (no authentication)
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPasswordPage />,
  },
  {
    path: "/reset-password",
    element: <ResetPasswordPage />,
  },
  {
    path: "/accept-invitation",
    element: <AcceptInvitationPage />,
  },
  {
    path: "/unauthorized",
    element: <UnauthorizedPage />,
  },
  {
    path: "/approve/:token",
    element: <PublicApprovalPage />,
  },
  // Parent auth (public, no layout)
  {
    path: "/parent/auth/:token",
    element: <ParentAuthPage />,
  },
  // Parent chat (auth via httpOnly cookie)
  {
    path: "/parent",
    element: <ParentLayout />,
    children: [
      { path: "chat", element: <ParentGroupListPage /> },
      { path: "chat/:groupId", element: <ParentChatPage /> },
    ],
  },
  {
    path: "/purchase",
    element: <PurchaseOrderPage />,
  },
  {
    path: "/purchase/:planSlug",
    element: <PurchaseOrderPage />,
  },
  {
    path: "/payment/success",
    element: <PaymentSuccessPage />,
  },
  {
    path: "/payment/error",
    element: <PaymentErrorPage />,
  },
  {
    path: "/renewal/:token",
    element: <RenewalPage />,
  },

  // Root redirect
  {
    path: "/",
    element: <Navigate to="/dashboard" replace />,
  },

  // Employee Portal routes (ROLE_EMPLOYEE only)
  {
    path: "/employee",
    element: (
      <RoleBasedRoute allowedRoles={["ROLE_EMPLOYEE"]}>
        <EmployeeLayout />
      </RoleBasedRoute>
    ),
    children: [
      { index: true, element: <EmployeeDashboardPage /> },
      { path: "timesheet", element: <EmployeeTimesheetPage /> },
      { path: "schedule", element: <EmployeeSchedulePage /> },
      { path: "chat", element: <EmployeeChatPage /> },
      { path: "chat/:groupId", element: <EmployeeChatGroupPage /> },
      { path: "help", element: <EmployeeHelpPage /> },
      { path: "profile", element: <ProfilePage /> },
    ],
  },

  // Super Admin routes (ROLE_SUPER_ADMIN only)
  {
    path: "/super-admin",
    element: (
      <RoleBasedRoute allowedRoles={["ROLE_SUPER_ADMIN"]}>
        <SuperAdminLayout />
      </RoleBasedRoute>
    ),
    children: [
      { index: true, element: <SuperAdminDashboardPage /> },
      { path: "tenants", element: <TenantListPage /> },
      { path: "tenants/:id", element: <TenantDetailPage /> },
      { path: "hives", element: <HivePlanListPage /> },
      { path: "profile", element: <ProfilePage /> },
      { path: "orders", element: <PurchaseOrderListPage /> },
      { path: "orders/:id", element: <PurchaseOrderDetailPage /> },
      { path: "stats", element: <StatsPage /> },
      { path: "health", element: <HealthCheckPage /> },
      { path: "holidays", element: <HolidayListPage /> },
      { path: "invoices", element: <InvoiceListPage /> },
      { path: "invoices/:id", element: <InvoiceDetailPage /> },
      { path: "subscriptions", element: <SubscriptionListPage /> },
      { path: "system-tasks", element: <SystemTasksPage /> },
    ],
  },

  // Admin routes (ROLE_ADMIN only)
  {
    path: "/dashboard",
    element: (
      <RoleBasedRoute allowedRoles={["ROLE_ADMIN"]}>
        <Layout>
          <DashboardPage />
        </Layout>
      </RoleBasedRoute>
    ),
  },
  {
    path: "/schedules",
    element: (
      <RoleBasedRoute allowedRoles={["ROLE_ADMIN"]}>
        <Layout>
          <ScheduleListPage />
        </Layout>
      </RoleBasedRoute>
    ),
  },
  {
    path: "/schedules/:id",
    element: (
      <RoleBasedRoute allowedRoles={["ROLE_ADMIN"]}>
        <Layout>
          <ScheduleDetailPage />
        </Layout>
      </RoleBasedRoute>
    ),
  },
  {
    path: "/schedules/:id/calendar",
    element: (
      <RoleBasedRoute allowedRoles={["ROLE_ADMIN"]}>
        <Layout>
          <ScheduleCalendarPage />
        </Layout>
      </RoleBasedRoute>
    ),
  },
  {
    path: "/schedules/:id/preview",
    element: (
      <RoleBasedRoute allowedRoles={["ROLE_ADMIN"]}>
        <Layout>
          <SchedulePreviewPage />
        </Layout>
      </RoleBasedRoute>
    ),
  },
  {
    path: "/schedules/:id/finalization",
    element: (
      <RoleBasedRoute allowedRoles={["ROLE_ADMIN"]}>
        <Layout>
          <FinalizationPage />
        </Layout>
      </RoleBasedRoute>
    ),
  },
  {
    path: "/employees",
    element: (
      <RoleBasedRoute allowedRoles={["ROLE_ADMIN"]}>
        <Layout>
          <EmployeeListPage />
        </Layout>
      </RoleBasedRoute>
    ),
  },
  {
    path: "/timesheet",
    element: (
      <RoleBasedRoute allowedRoles={["ROLE_ADMIN"]}>
        <Layout>
          <TimesheetListPage />
        </Layout>
      </RoleBasedRoute>
    ),
  },
  // Reports
  {
    path: "/reports/employee/:teacherId",
    element: (
      <RoleBasedRoute allowedRoles={["ROLE_ADMIN"]}>
        <Layout>
          <EmployeeReportPage />
        </Layout>
      </RoleBasedRoute>
    ),
  },
  {
    path: "/reports/:tab",
    element: (
      <RoleBasedRoute allowedRoles={["ROLE_ADMIN"]}>
        <Layout>
          <ReportsPage />
        </Layout>
      </RoleBasedRoute>
    ),
  },
  {
    path: "/reports",
    element: (
      <RoleBasedRoute allowedRoles={["ROLE_ADMIN"]}>
        <Layout>
          <ReportsPage />
        </Layout>
      </RoleBasedRoute>
    ),
  },
  // Profitability feature hidden - not included in current scope
  {
    path: "/profitability",
    element: <Navigate to="/dashboard" replace />,
  },
  {
    path: "/substitutions",
    element: (
      <RoleBasedRoute allowedRoles={["ROLE_ADMIN"]}>
        <Layout>
          <SubstitutionsListPage />
        </Layout>
      </RoleBasedRoute>
    ),
  },
  {
    path: "/substitutions/departures/:id",
    element: (
      <RoleBasedRoute allowedRoles={["ROLE_ADMIN"]}>
        <Layout>
          <DepartureDetailsPage />
        </Layout>
      </RoleBasedRoute>
    ),
  },
  {
    path: "/substitutions/:id",
    element: (
      <RoleBasedRoute allowedRoles={["ROLE_ADMIN"]}>
        <Layout>
          <SubstitutionDetailsPage />
        </Layout>
      </RoleBasedRoute>
    ),
  },
  {
    path: "/chat",
    element: (
      <RoleBasedRoute allowedRoles={["ROLE_ADMIN"]}>
        <Layout>
          <AdminChatPage />
        </Layout>
      </RoleBasedRoute>
    ),
  },
  {
    path: "/chat/:groupId",
    element: (
      <RoleBasedRoute allowedRoles={["ROLE_ADMIN"]}>
        <Layout>
          <AdminChatGroupPage />
        </Layout>
      </RoleBasedRoute>
    ),
  },
  {
    path: "/dictionary-data",
    element: (
      <RoleBasedRoute allowedRoles={["ROLE_ADMIN"]}>
        <Layout>
          <DictionaryDataPage />
        </Layout>
      </RoleBasedRoute>
    ),
  },
  {
    path: "/dictionary-data/:tab",
    element: (
      <RoleBasedRoute allowedRoles={["ROLE_ADMIN"]}>
        <Layout>
          <DictionaryDataPage />
        </Layout>
      </RoleBasedRoute>
    ),
  },
  // Profile accessible to all authenticated users
  {
    path: "/profile",
    element: (
      <ProtectedRoute>
        <Layout>
          <ProfilePage />
        </Layout>
      </ProtectedRoute>
    ),
  },
  // Demo Help (pomoc w trybie demo)
  {
    path: "/demo-help",
    element: (
      <ProtectedRoute>
        <Layout>
          <DemoHelpPage />
        </Layout>
      </ProtectedRoute>
    ),
  },
  // Help (pomoc dla zalogowanych adminów)
  {
    path: "/help",
    element: (
      <RoleBasedRoute allowedRoles={["ROLE_ADMIN"]}>
        <Layout>
          <HelpPage />
        </Layout>
      </RoleBasedRoute>
    ),
  },
  // Parent Approval routes (admin only)
  {
    path: "/schedules/:scheduleId/parent-approvals",
    element: (
      <RoleBasedRoute allowedRoles={["ROLE_ADMIN"]}>
        <Layout>
          <ParentApprovalsPage />
        </Layout>
      </RoleBasedRoute>
    ),
  },
  {
    path: "/schedules/:scheduleId/parent-approvals/:processId",
    element: (
      <RoleBasedRoute allowedRoles={["ROLE_ADMIN"]}>
        <Layout>
          <ParentApprovalDetailsPage />
        </Layout>
      </RoleBasedRoute>
    ),
  },
], {
  future: {
    v7_relativeSplatPath: true,
    v7_fetcherPersist: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_skipActionErrorRevalidation: true,
  },
});
